import { AppName } from '@savgroup-front-common/constants';

import {
  getReversTranslateConfiguration,
  isDynamicTermSuggestionEnabled,
} from './dynamicTermSuggestion.configuration';
import { buildTermSuggestionOrigin } from './dynamicTermSuggestion.formatters';
import { TERM_SUGGESTION_SEND_STATUS } from './dynamicTermSuggestion.types';
import {
  getPendingDynamicTermBatches,
  persistDynamicTerms,
  sweepExpiredDynamicTerms,
} from './dynamicTermSuggestionStore';
import { postTermSuggestions } from './postTermSuggestions';

let hostAppName: AppName | undefined;
let isCircuitBroken = false;
let syncIntervalId: ReturnType<typeof setInterval> | undefined;
let isSyncRunning = false;

const synchronizeDynamicTerms = async (): Promise<void> => {
  if (isCircuitBroken || isSyncRunning) {
    return;
  }

  isSyncRunning = true;

  try {
    sweepExpiredDynamicTerms();

    await getPendingDynamicTermBatches().reduce(async (previous, keys) => {
      await previous;

      if (isCircuitBroken) {
        return;
      }

      const result = await postTermSuggestions({
        keys,
        origin: buildTermSuggestionOrigin({ appName: hostAppName }),
      });

      if (result.status === TERM_SUGGESTION_SEND_STATUS.CIRCUIT_BROKEN) {
        isCircuitBroken = true;
      }
    }, Promise.resolve());

    persistDynamicTerms();
  } finally {
    isSyncRunning = false;
  }
};

const flushDynamicTermsBeforeUnload = (): void => {
  if (isCircuitBroken) {
    return;
  }

  const [firstBatch] = getPendingDynamicTermBatches();

  if (!firstBatch?.length) {
    return;
  }

  postTermSuggestions({
    keys: firstBatch,
    origin: buildTermSuggestionOrigin({ appName: hostAppName }),
    shouldKeepAlive: true,
  });
};

const handleVisibilityChange = (): void => {
  if (document.visibilityState !== 'hidden') {
    return;
  }

  flushDynamicTermsBeforeUnload();
};

export const startDynamicTermSuggestionSync = ({
  appName,
}: {
  appName?: AppName;
} = {}): void => {
  if (syncIntervalId || !isDynamicTermSuggestionEnabled()) {
    return;
  }

  const configuration = getReversTranslateConfiguration();

  if (!configuration) {
    return;
  }

  hostAppName = appName;

  syncIntervalId = setInterval(
    synchronizeDynamicTerms,
    configuration.syncIntervalInMilliseconds,
  );

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('pagehide', flushDynamicTermsBeforeUnload);
};

export const stopDynamicTermSuggestionSync = (): void => {
  if (syncIntervalId) {
    clearInterval(syncIntervalId);
    syncIntervalId = undefined;
  }

  document.removeEventListener('visibilitychange', handleVisibilityChange);
  window.removeEventListener('pagehide', flushDynamicTermsBeforeUnload);
};

export const resetDynamicTermSuggestionSchedulerForTests = (): void => {
  stopDynamicTermSuggestionSync();
  hostAppName = undefined;
  isCircuitBroken = false;
  isSyncRunning = false;
};
